/* eslint-disable react-refresh/only-export-components */
import { useState, useCallback, useRef, useEffect, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react'
import {
  ToastContext,
  type Toast,
  type ToastType,
  type ToastContextType,
  type ToastActionOptions,
} from '../contexts/ToastContext'
import { initToast } from '../utils/toast'
import './Toast.css'

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const removeToast = useCallback((id: string) => {
    // Clear timeout if it exists
    const timeoutId = timeoutRefs.current.get(id)
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutRefs.current.delete(id)
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const clearAllToasts = useCallback(() => {
    // Clear all timeouts
    timeoutRefs.current.forEach((timeoutId) => {
      clearTimeout(timeoutId)
    })
    timeoutRefs.current.clear()
    setToasts([])
  }, [])

  const showToast = useCallback(
    (type: ToastType, message: string, duration = 5000, onClick?: () => void) => {
      setToasts((prev) => {
        // Check if a toast with same type + message already exists
        const existingIndex = prev.findIndex((t) => t.type === type && t.message === message)

        if (existingIndex !== -1) {
          // Increment count on existing toast
          const updated = [...prev]
          const existing = updated[existingIndex]
          updated[existingIndex] = { ...existing, count: existing.count + 1, onClick: onClick ?? existing.onClick }

          // Reset the timeout for the existing toast
          const existingTimeout = timeoutRefs.current.get(existing.id)
          if (existingTimeout) {
            clearTimeout(existingTimeout)
          }

          if (duration > 0) {
            const timeoutId = setTimeout(() => {
              removeToast(existing.id)
              timeoutRefs.current.delete(existing.id)
            }, duration)
            timeoutRefs.current.set(existing.id, timeoutId)
          }

          return updated
        }

        // Create new toast
        const id = crypto.randomUUID()
        const toast: Toast = { id, type, message, duration, count: 1, onClick }

        if (duration > 0) {
          const timeoutId = setTimeout(() => {
            removeToast(id)
            timeoutRefs.current.delete(id)
          }, duration)
          timeoutRefs.current.set(id, timeoutId)
        }

        return [...prev, toast]
      })
    },
    [removeToast]
  )

  // Cleanup all timeouts on unmount
  useEffect(() => {
    const currentTimeouts = timeoutRefs.current
    return () => {
      currentTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId)
      })
      currentTimeouts.clear()
    }
  }, [])

  const success = useCallback(
    (message: string, duration?: number) => {
      showToast('success', message, duration)
    },
    [showToast]
  )

  const error = useCallback(
    (message: string, duration?: number) => {
      showToast('error', message, duration)
    },
    [showToast]
  )

  const warning = useCallback(
    (message: string, duration?: number) => {
      showToast('warning', message, duration)
    },
    [showToast]
  )

  const info = useCallback(
    (message: string, durationOrOptions?: number | ToastActionOptions) => {
      if (typeof durationOrOptions === 'object' && durationOrOptions !== null) {
        showToast('info', message, durationOrOptions.duration ?? 5000, durationOrOptions.onClick)
        return
      }
      showToast('info', message, durationOrOptions)
    },
    [showToast]
  )

  // CRITICAL: Memoize the context value to prevent unnecessary re-renders
  // Without this, every toast change causes all useToast consumers to re-render,
  // which can cascade into issues like AuthContext re-running loadUser
  const contextValue = useMemo<ToastContextType>(
    () => ({ showToast, success, error, warning, info }),
    [showToast, success, error, warning, info]
  )

  // Wire the utils/toast singleton to the real context so mutation hooks
  // outside React components (e.g. useTestConnectionMutation) can show toasts.
  useEffect(() => {
    initToast({ success, error, warning, info, showToast })
  }, [success, error, warning, info, showToast])

  const showClearAll = toasts.length >= 2

  // Render toast container via portal to document.body so it appears above modals
  // (modals also use portals, and #root has isolation:isolate which creates a stacking context)
  const toastContainer = (
    <div className="toast-container">
      {showClearAll && (
        <button
          className="toast-clear-all"
          onClick={clearAllToasts}
          aria-label="Clear all notifications"
        >
          Clear all ({toasts.length})
        </button>
      )}
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast--${toast.type}${toast.onClick ? ' toast--clickable' : ''}`}
          onClick={toast.onClick}
          onKeyDown={
            toast.onClick
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    toast.onClick?.()
                  }
                }
              : undefined
          }
          role={toast.onClick ? 'button' : undefined}
          tabIndex={toast.onClick ? 0 : undefined}
        >
          <div className="toast__icon">
            {toast.type === 'success' && <CheckCircle2 size={16} strokeWidth={2.5} />}
            {toast.type === 'error' && <XCircle size={16} strokeWidth={2.5} />}
            {toast.type === 'warning' && <AlertCircle size={16} strokeWidth={2.5} />}
            {toast.type === 'info' && <Info size={16} strokeWidth={2.5} />}
          </div>
          <div className="toast__message">
            {toast.message}
            {toast.count > 1 && <span className="toast__count"> ×{toast.count}</span>}
          </div>
          <button className="toast__close" onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }} aria-label="Close">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  )

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(toastContainer, document.body)}
    </ToastContext.Provider>
  )
}
